import type {
  CreateBeneficiaryRequest,
  CreateBlockchainBeneficiaryApiArg,
} from "@generated/redux/services/generated/api";
import {
  type AccountBankAccount,
  type AccountBeneficiaryAddress,
  BeneficiarySource,
  type BeneficiaryTemplate,
  type CopCheckResponse,
  CopMatchResult,
  CopStatus,
  LegalEntityType,
  WalletType,
} from "@generated/types";

/**
 * The create payloads for both recipient arms, built from plain values so the
 * stripping below is unit-tested (buildCreateRequests.test.ts).
 *
 * Why stripping lives here and only here: the details forms live above the
 * step-keyed animation (RecipientFormsProvider) and react-hook-form defaults to
 * shouldUnregister:false, so `handleSubmit` data includes every field the user
 * ever typed, rendered or not. A Person → Business switch leaves firstName /
 * lastName in it; a VASP → self-hosted switch leaves the VASP id. The user
 * can't see those values any more, so they must not reach the wire. Field-level
 * shouldUnregister is not the fix: it would also wipe the names on every Back.
 */

/** Maps a CoP (Confirmation of Payee) match result to the name-confirmation status. */
export function mapCopResultToNameConfirmation(
  status: CopMatchResult
): CopStatus {
  switch (status) {
    case CopMatchResult.Match:
    case CopMatchResult.CloseMatch:
      return CopStatus.Correct;
    case CopMatchResult.NoMatch:
      return CopStatus.Incorrect;
    // NotSupported: no check was attempted (non-GBP or non-UK). The
    // comment's NOT_SUPPORTED prefix records that, not a failed check.
    // default: a status this build doesn't know, shown as Unavailable by the
    // modal. The raw value still reaches the comment.
    case CopMatchResult.Unknown:
    case CopMatchResult.Unavailable:
    case CopMatchResult.NotSupported:
    default:
      return CopStatus.Unknown;
  }
}

export type FiatCreateInput = {
  /** The account the entry was pinned to when it started (never the live one). */
  customerAccountId: string;
  currency: string;
  country: string;
  beneficiaryType: LegalEntityType;
  name: string;
  bankAccount: Partial<AccountBankAccount>;
  /**
   * The template the saved bank account was built from. Not the effect-driven
   * selection on the account step, which re-filters (and can move) whenever
   * beneficiaryType changes — including a CoP accept-suggestion flip while
   * that step is fading out.
   */
  savedTemplate: Pick<BeneficiaryTemplate, "defaultOperationCode"> | null;
  purposeCode: string;
  copCheck: { result: CopCheckResponse; userAction: string } | null;
  /** The verification date, `YYYY-MM-DD`. */
  today: string;
  details: {
    reference?: string;
    nickname?: string;
    address?: Partial<AccountBeneficiaryAddress>;
  };
};

/** The names that belong to the selected entity type, and nothing else. */
function scopeAddressToEntity(
  address: Partial<AccountBeneficiaryAddress>,
  beneficiaryType: LegalEntityType
): Partial<AccountBeneficiaryAddress> {
  return {
    ...address,
    ...(beneficiaryType === LegalEntityType.Person
      ? { corporationName: undefined }
      : { firstName: undefined, lastName: undefined }),
  };
}

export function buildFiatCreateRequest({
  customerAccountId,
  currency,
  country,
  beneficiaryType,
  name,
  bankAccount,
  savedTemplate,
  purposeCode,
  copCheck,
  today,
  details,
}: FiatCreateInput): CreateBeneficiaryRequest {
  const { reference, nickname, address } = details;

  return {
    customerAccount: customerAccountId,
    currency,
    beneficiaryType,
    name,
    nickname: nickname || undefined,
    bankAccount: {
      accountName: bankAccount.accountName || "",
      country,
      accountNumber: bankAccount.accountNumber,
      nationalClearingCode: bankAccount.nationalClearingCode,
      iban: bankAccount.iban,
      bic: bankAccount.bic,
      accountType: BeneficiarySource.Beneficiary,
    },
    defaults: {
      operationCode: savedTemplate?.defaultOperationCode,
      ...(reference && { remittanceInformation: reference }),
      ...(purposeCode && { purposeCode }),
    },
    ...(copCheck && {
      verification: {
        nameConfirmation: mapCopResultToNameConfirmation(
          copCheck.result.status
        ),
        lastChecked: today,
        comment: [
          copCheck.result.status,
          copCheck.result.statusDescription,
          copCheck.userAction,
        ]
          .filter(Boolean)
          .join(" | "),
      },
    }),
    ...(address && { address: scopeAddressToEntity(address, beneficiaryType) }),
  };
}

/** The travel-rule identity collected on the blockchain address step. */
export type BlockchainTravelRuleAddress = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  line1?: string;
  line2?: string;
  postTown?: string;
  state?: string;
  postCode?: string;
  country?: string;
};

export type BlockchainCreateInput = {
  /** The account the entry was pinned to when it started (never the live one). */
  customerAccountId: string;
  /** The virtual-asset UUID the Destination step resolved for asset + network. */
  assetUuid: string;
  beneficiaryType: LegalEntityType | null;
  travelRuleAddress: BlockchainTravelRuleAddress;
  details: {
    address: string;
    walletType: WalletType;
    vaspId?: string;
    vaspName?: string;
    vaspAdditionalInfo?: string;
    nickname?: string;
  };
};

export type BlockchainCreateRequest = CreateBlockchainBeneficiaryApiArg["body"];

/** The "Other" option in the VASP picker: a free-text VASP, not a UUID. */
export const OTHER_VASP_ID = "other";

export function buildBlockchainCreateRequest({
  customerAccountId,
  assetUuid,
  beneficiaryType,
  travelRuleAddress,
  details,
}: BlockchainCreateInput): BlockchainCreateRequest {
  const {
    address,
    walletType,
    vaspId,
    vaspName,
    vaspAdditionalInfo,
    nickname,
  } = details;

  const request: BlockchainCreateRequest = {
    customerAccount: customerAccountId,
    asset: assetUuid,
    address,
    walletType,
    active: true,
    ...(nickname && { nickname }),
  };

  // VASP fields only for a VASP-hosted wallet, and only the branch the picker
  // shows: a resolved VASP sends its UUID, "Other" sends the free text.
  if (walletType === WalletType.Vasp) {
    if (vaspId && vaspId !== OTHER_VASP_ID) {
      request.vasp = vaspId;
    } else if (vaspId === OTHER_VASP_ID) {
      const vaspDetails = [vaspName, vaspAdditionalInfo]
        .filter(Boolean)
        .join("\n\n");
      if (vaspDetails) request.vaspDetails = vaspDetails;
    }
  }

  const { country, postTown } = travelRuleAddress;
  if (country && postTown) {
    const addressLines = [
      travelRuleAddress.line1,
      travelRuleAddress.line2,
    ].filter((line): line is string => !!line);
    const isPerson = beneficiaryType === LegalEntityType.Person;

    request.legalEntity = {
      type: isPerson ? LegalEntityType.Person : LegalEntityType.Corporation,
      townName: postTown,
      country,
      ...(isPerson && {
        ...(travelRuleAddress.firstName && {
          firstName: travelRuleAddress.firstName,
        }),
        ...(travelRuleAddress.lastName && {
          lastName: travelRuleAddress.lastName,
        }),
      }),
      ...(beneficiaryType === LegalEntityType.Corporation &&
        travelRuleAddress.companyName && {
          companyName: travelRuleAddress.companyName,
        }),
      ...(travelRuleAddress.state && {
        countrySubDivision: travelRuleAddress.state,
      }),
      ...(travelRuleAddress.postCode && {
        postCode: travelRuleAddress.postCode,
      }),
      ...(addressLines.length > 0 && { addressLine: addressLines }),
    };
  }

  return request;
}
